/**
 * Button — 5 roles × 3 sizes, per theme T5a.
 *
 * Role:  primary (navy, the one primary colour) | secondary | tertiary
 *        (gold text) | destructive | accent
 * Size:  sm (28 px) | md (36 px, default) | lg (44 px)
 *
 * Loading state keeps the button visible but swaps the leading icon for
 * a spinner and disables interaction (per T5a §1.9).  `leftIcon` +
 * `rightIcon` slots accept any lucide-react icon component.
 */
import type { ButtonHTMLAttributes, ComponentType, ReactNode } from 'react';
import { forwardRef } from 'react';

import { cn } from './cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  rightIcon?: ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  /** When true, the button stretches to fill its container's width. */
  fullWidth?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Navy with the shell's gold edge — the one primary colour (the shell's
  // "+ New" uses the same tokens). Blue brand-primary is not a button fill.
  primary:
    'border border-gold bg-action text-action-fg hover:bg-action-hover active:bg-action-active disabled:bg-action',
  secondary:
    'bg-transparent text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100 hover:border-slate-400',
  tertiary:
    'bg-transparent text-gold-text hover:bg-slate-50 hover:text-gold-deep rounded-md',
  destructive:
    'bg-error-600 text-white hover:bg-error-700 active:bg-[#991B1B] disabled:bg-error-600',
  accent:
    'bg-brand-accent-500 text-brand-accent-900 hover:bg-brand-accent-600 active:bg-brand-accent-700',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-7 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-base gap-2',
  lg: 'h-11 px-6 text-base gap-2',
};

const ICON_SIZE: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

const BASE_CLASSES =
  'duration-DEFAULT inline-flex items-center justify-center rounded-lg font-medium transition-colors ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-disabled';

/**
 * The Button's classes, for an element that must not be a `<button>` — a
 * `Link` whose job is navigation (a `<button>` inside an `<a>` is invalid).
 *
 *     <Link className={buttonClassName({ variant: 'secondary' })} href="/x">…</Link>
 */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
} = {}): string {
  return cn(
    BASE_CLASSES,
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    fullWidth && 'w-full',
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leftIcon: LeftIcon,
    rightIcon: RightIcon,
    fullWidth = false,
    className,
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const iconSize = ICON_SIZE[size];
  const busy = isLoading || disabled;

  return (
    <button
      ref={ref}
      aria-busy={isLoading || undefined}
      className={buttonClassName({ variant, size, fullWidth, className })}
      disabled={busy}
      type={type}
      {...rest}
    >
      {isLoading ? (
        <Spinner size={iconSize} />
      ) : LeftIcon ? (
        <LeftIcon aria-hidden size={iconSize} strokeWidth={1.75} />
      ) : null}
      {children}
      {!isLoading && RightIcon ? (
        <RightIcon aria-hidden size={iconSize} strokeWidth={1.75} />
      ) : null}
    </button>
  );
});
